import { z } from 'zod';
import { ReadinessSchema } from './intake.js';
import { ConsentKindSchema } from './notifications.js';
import { PAGE_LIMIT_DEFAULT, PAGE_LIMIT_MAX, PageInfoSchema } from './pagination.js';
/**
 * Scheduling contracts (M4.1, M4.2, M4.3, M4.4, M4.12).
 *
 * This file is the promoted copy of `apps/admin/src/features/scheduling/types.ts`,
 * which the admin UI wrote against `Docs/DATABASE.md` §7.3 and `Docs/API.md` §2.7
 * while the backend did not have these shapes yet (`Docs/SCHEDULING_UI.md` §2). Field
 * names are unchanged, so the admin seam's move is an import swap and nothing above
 * `apps/admin/src/lib/api/scheduling.ts` changes.
 *
 * Two of the UI's shapes were open questions; both are answered here and in the
 * database, and the reasons are worth keeping next to the schemas:
 *
 * 1. **Alternate weeks are anchored to a date, not to ISO week parity** (Q-T22,
 *    `SCHEDULING_UI.md` §5.1). `provider_schedules.week_parity` is `even`/`odd` on the
 *    ISO week number, and ISO week numbers do not alternate cleanly: a 53-week year
 *    puts two odd weeks back to back and a Friday alternation silently flips. The
 *    stored form is `{ weekday, startDateLocal }` in `provider_schedules.week_anchor_date`;
 *    parity is **derived** and kept only so an older reader still sees something sane.
 * 2. **Buffers and slot granularity exist on the provider as well as the type**
 *    (`SCHEDULING_UI.md` §5.3). `providers` carries `default_buffer_before_minutes`,
 *    `default_buffer_after_minutes` and `default_slot_granularity_minutes`, and
 *    `appointment_types.slot_granularity_minutes` became nullable ("follow the
 *    provider"). The engine resolves buffer = max(type, provider) and
 *    granularity = type ?? provider.
 *
 * Times are `HH:MM` **local wall clock** and dates are `YYYY-MM-DD` **local calendar**.
 * Neither is ever an instant; only the engine's output carries instants (API.md §1.2).
 */
/* ------------------------------------------------------------------ primitives ---- */
/** `0` is Sunday, matching `provider_schedules.weekday`. */
export const WeekdaySchema = z.union([
    z.literal(0),
    z.literal(1),
    z.literal(2),
    z.literal(3),
    z.literal(4),
    z.literal(5),
    z.literal(6),
]);
export const WEEKDAYS = [0, 1, 2, 3, 4, 5, 6];
/** Monday first, which is how a clinic reads a week. */
export const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0];
export const WEEKDAY_NAMES = Object.freeze({
    0: 'Sunday',
    1: 'Monday',
    2: 'Tuesday',
    3: 'Wednesday',
    4: 'Thursday',
    5: 'Friday',
    6: 'Saturday',
});
/** Working hours, breaks and durations move in five-minute steps (M4.11 validation). */
export const TIME_STEP_MINUTES = 5;
/** A local wall-clock time, `HH:MM`, 24 hour. Never an instant. */
export const LocalTimeSchema = z
    .string()
    .regex(/^([01][0-9]|2[0-3]):([0-5][0-9])$/, 'Enter a time as HH:MM.');
/** A local calendar date, `YYYY-MM-DD`. Never an instant. */
export const LocalDateSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Enter a date as YYYY-MM-DD.')
    .refine((value) => !Number.isNaN(Date.parse(`${value}T12:00:00Z`)), 'That is not a real date.');
/** Two-letter US state code, uppercase. `providers.states_licensed`, `patients.state`. */
export const StateCodeSchema = z
    .string()
    .length(2)
    .transform((value) => value.toUpperCase())
    .pipe(z.string().regex(/^[A-Z]{2}$/, 'Use a two-letter state code.'));
/** IANA zone. Validated against the runtime's own database, not a hand-written list. */
export const TimeZoneSchema = z
    .string()
    .min(1)
    .refine(isIanaZone, 'That is not a known time zone.');
export function isIanaZone(value) {
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: value });
        return true;
    }
    catch {
        return false;
    }
}
/* -------------------------------------------------------------------- schedules ---- */
/** A break inside a working day. One entry of `provider_schedules.breaks`. */
export const BreakRangeSchema = z.object({
    start: LocalTimeSchema,
    end: LocalTimeSchema,
});
/** One row of the weekly grid. Mirrors one `provider_schedules` row per weekday. */
export const ScheduleDaySchema = z.object({
    weekday: WeekdaySchema,
    /** False means no `provider_schedules` row for this weekday at all. */
    working: z.boolean(),
    startLocal: LocalTimeSchema,
    endLocal: LocalTimeSchema,
    breaks: z.array(BreakRangeSchema).max(6),
});
/**
 * "Every other Friday, starting 18 September" (Q-T22).
 *
 * The anchor is a date whose weekday equals `weekday`; the provider works that weekday
 * on the anchor and every second week after it, and not on the weeks between. Dates
 * before the anchor are not worked, because the rule describes an arrangement that
 * starts then.
 */
export const AlternateWeekRuleSchema = z.object({
    /** `provider_schedules.id` of the row carrying the anchor. */
    id: z.string(),
    weekday: WeekdaySchema,
    startDateLocal: LocalDateSchema,
    reason: z.string().max(200).default(''),
});
export const ExceptionKindSchema = z.enum(['closed', 'custom_hours', 'extra_hours']);
/**
 * A date-specific override (`schedule_exceptions`).
 *
 * `endDateLocal` is the inclusive last day of a range closure. `SCHEDULING_UI.md` §5.2
 * asked whether ranges should be one row or many; they are **one row with an end date**
 * (M4.2), because the alternative loses the fact that a conference was one decision and
 * makes the list unreadable on read-back.
 */
export const ScheduleExceptionSchema = z.object({
    id: z.string(),
    /** Null is the whole clinic - `POST /admin/schedule-exceptions`. */
    providerId: z.string().nullable(),
    dateLocal: LocalDateSchema,
    endDateLocal: LocalDateSchema.nullable(),
    kind: ExceptionKindSchema,
    startLocal: LocalTimeSchema.nullable(),
    endLocal: LocalTimeSchema.nullable(),
    /** Operational only. Never a diagnosis, never anything about a patient. */
    reason: z.string().max(200).default(''),
});
/* -------------------------------------------------------------------- providers ---- */
/** One row of `/providers`. `GET /admin/providers`. */
export const ProviderSummarySchema = z.object({
    id: z.string(),
    displayName: z.string().min(1).max(120),
    credentials: z.string().max(40),
    npi: z.string().nullable(),
    statesLicensed: z.array(z.string()),
    appointmentTypeIds: z.array(z.string()),
    telehealthUrl: z.string().nullable(),
    /** Tebra `ProviderID`. Written by the EHR matcher (M9); read-only on the screen. */
    ehrExternalId: z.string().nullable(),
    timeZone: z.string(),
    isActive: z.boolean(),
});
/** Provider-level scheduling defaults (`SCHEDULING_UI.md` §5.3, resolved in M4.1). */
export const ProviderSchedulingDefaultsSchema = z.object({
    bufferBeforeMinutes: z.number().int().min(0).max(240),
    bufferAfterMinutes: z.number().int().min(0).max(240),
    slotGranularityMinutes: z.number().int().min(5).max(240),
});
/** `GET /admin/providers/:id` → `{ provider, schedules, exceptions }`, plus the defaults. */
export const ProviderDetailSchema = z.object({
    provider: ProviderSummarySchema,
    defaults: ProviderSchedulingDefaultsSchema,
    /** Always seven rows, indexed by `weekday`. */
    schedule: z.array(ScheduleDaySchema).length(7),
    exceptions: z.array(ScheduleExceptionSchema),
    alternateWeeks: z.array(AlternateWeekRuleSchema),
});
export const CreateProviderRequestSchema = z.object({
    displayName: z.string().min(1).max(120),
    credentials: z.string().max(40).default(''),
    npi: z
        .string()
        .regex(/^\d{10}$/, 'An NPI is ten digits.')
        .nullable()
        .default(null),
    statesLicensed: z.array(StateCodeSchema).min(1, 'Pick at least one state.'),
    appointmentTypeIds: z.array(z.uuid()).default([]),
    telehealthUrl: z
        .url()
        .startsWith('https://', 'The link must use https://.')
        .nullable()
        .default(null),
    ehrExternalId: z.string().max(64).nullable().default(null),
    timeZone: TimeZoneSchema.optional(),
    acceptsNewPatients: z.boolean().default(true),
    bio: z.string().max(2000).nullable().default(null),
    sortOrder: z.number().int().min(0).max(9999).default(0),
    defaults: ProviderSchedulingDefaultsSchema.optional(),
});
/**
 * `PATCH /admin/providers/:id`. Every field optional; the server merges.
 *
 * Written out rather than `CreateProviderRequestSchema.partial()`, and the reason is a
 * trap worth naming: `.partial()` leaves a field's `.default()` in place, so a PATCH
 * carrying only `{ bio }` would parse to an object with `appointmentTypeIds: []` - and
 * the service, seeing a value, would replace the provider's visit-type links with
 * nothing. A partial update must be able to tell "absent" from "empty", which means no
 * defaults on this schema at all.
 */
export const UpdateProviderRequestSchema = z.object({
    displayName: z.string().min(1).max(120).optional(),
    credentials: z.string().max(40).optional(),
    npi: z
        .string()
        .regex(/^\d{10}$/, 'An NPI is ten digits.')
        .nullable()
        .optional(),
    statesLicensed: z.array(StateCodeSchema).min(1, 'Pick at least one state.').optional(),
    appointmentTypeIds: z.array(z.uuid()).optional(),
    telehealthUrl: z
        .url()
        .startsWith('https://', 'The link must use https://.')
        .nullable()
        .optional(),
    ehrExternalId: z.string().max(64).nullable().optional(),
    timeZone: TimeZoneSchema.optional(),
    acceptsNewPatients: z.boolean().optional(),
    bio: z.string().max(2000).nullable().optional(),
    sortOrder: z.number().int().min(0).max(9999).optional(),
    defaults: ProviderSchedulingDefaultsSchema.optional(),
    isActive: z.boolean().optional(),
});
/* ------------------------------------------------------------- appointment types ---- */
/** `appointment_types`. Prices are cents; the dollar input is a rendering of them. */
export const AppointmentTypeSchema = z.object({
    id: z.string(),
    key: z.string(),
    name: z.string(),
    /** Patient-facing. Shown in the booking funnel next to the price. */
    description: z.string(),
    durationMinutes: z.number().int().positive(),
    bufferBeforeMinutes: z.number().int().min(0),
    bufferAfterMinutes: z.number().int().min(0),
    /** Null follows the provider's default granularity. */
    slotGranularityMinutes: z.number().int().min(5).nullable(),
    minLeadHours: z.number().int().min(0),
    maxHorizonDays: z.number().int().min(1),
    selfPayPriceCents: z.number().int().min(0),
    currency: z.string().length(3),
    requiresInsuranceStep: z.boolean(),
    ehrReasonId: z.string().nullable(),
    ehrServiceLocationId: z.string().nullable(),
    intakeFormId: z.string().nullable(),
    isActive: z.boolean(),
    sortOrder: z.number().int(),
    providerIds: z.array(z.string()),
});
const onStep = (minutes) => minutes % TIME_STEP_MINUTES === 0;
export const CreateAppointmentTypeRequestSchema = z.object({
    key: z
        .string()
        .min(1)
        .max(64)
        .regex(/^[a-z0-9][a-z0-9-]*$/, 'Use lower-case letters, digits and hyphens.'),
    name: z.string().min(1).max(120),
    description: z.string().max(2000).default(''),
    durationMinutes: z
        .number()
        .int()
        .positive()
        .max(480)
        .refine(onStep, `Duration must be on a ${String(TIME_STEP_MINUTES)}-minute step.`),
    bufferBeforeMinutes: z.number().int().min(0).max(240).default(0),
    bufferAfterMinutes: z.number().int().min(0).max(240).default(0),
    slotGranularityMinutes: z.number().int().min(5).max(240).nullable().default(null),
    minLeadHours: z.number().int().min(0).max(8760).default(24),
    maxHorizonDays: z.number().int().min(1).max(365).default(60),
    selfPayPriceCents: z.number().int().min(0).max(10_000_000).default(0),
    currency: z.string().length(3).default('USD'),
    requiresInsuranceStep: z.boolean().default(true),
    ehrReasonId: z.string().max(64).nullable().default(null),
    ehrServiceLocationId: z.string().max(64).nullable().default(null),
    intakeFormId: z.uuid().nullable().default(null),
    sortOrder: z.number().int().min(0).max(9999).default(0),
    providerIds: z.array(z.uuid()).default([]),
});
/** `PATCH /admin/appointment-types/:id`. No defaults - see `UpdateProviderRequestSchema`. */
export const UpdateAppointmentTypeRequestSchema = z.object({
    /** Accepted so an unchanged round-trip does not fail; a *different* key is refused. */
    key: z.string().min(1).max(64).optional(),
    name: z.string().min(1).max(120).optional(),
    description: z.string().max(2000).optional(),
    durationMinutes: z
        .number()
        .int()
        .positive()
        .max(480)
        .refine(onStep, `Duration must be on a ${String(TIME_STEP_MINUTES)}-minute step.`)
        .optional(),
    bufferBeforeMinutes: z.number().int().min(0).max(240).optional(),
    bufferAfterMinutes: z.number().int().min(0).max(240).optional(),
    slotGranularityMinutes: z.number().int().min(5).max(240).nullable().optional(),
    minLeadHours: z.number().int().min(0).max(8760).optional(),
    maxHorizonDays: z.number().int().min(1).max(365).optional(),
    selfPayPriceCents: z.number().int().min(0).max(10_000_000).optional(),
    currency: z.string().length(3).optional(),
    requiresInsuranceStep: z.boolean().optional(),
    ehrReasonId: z.string().max(64).nullable().optional(),
    ehrServiceLocationId: z.string().max(64).nullable().optional(),
    intakeFormId: z.uuid().nullable().optional(),
    sortOrder: z.number().int().min(0).max(9999).optional(),
    providerIds: z.array(z.uuid()).optional(),
    isActive: z.boolean().optional(),
});
/* ------------------------------------------------------------ schedule requests ---- */
/** `PUT /admin/providers/:id/schedules` - the whole week, replaced at once. */
export const SaveScheduleRequestSchema = z.object({
    /** The date the new week starts applying. `provider_schedules.effective_from`. */
    effectiveFrom: LocalDateSchema,
    days: z.array(ScheduleDaySchema).max(7),
    alternateWeeks: z
        .array(AlternateWeekRuleSchema.omit({ id: true }).extend({ id: z.string().optional() }))
        .max(7)
        .default([]),
    defaults: ProviderSchedulingDefaultsSchema.optional(),
});
export const CreateExceptionRequestSchema = z
    .object({
    dateLocal: LocalDateSchema,
    endDateLocal: LocalDateSchema.nullable().default(null),
    kind: ExceptionKindSchema,
    startLocal: LocalTimeSchema.nullable().default(null),
    endLocal: LocalTimeSchema.nullable().default(null),
    reason: z.string().max(200).default(''),
})
    .refine((value) => value.endDateLocal === null || value.endDateLocal >= value.dateLocal, {
    message: 'A closure must end on or after the day it starts.',
    path: ['endDateLocal'],
})
    .refine((value) => value.kind === 'closed' ||
    (value.startLocal !== null && value.endLocal !== null && value.endLocal > value.startLocal), { message: 'Custom and extra hours need a start and an end.', path: ['startLocal'] });
/** `POST /admin/schedule-exceptions` - clinic-wide closure (`provider_id` null). */
export const CreateClinicExceptionRequestSchema = CreateExceptionRequestSchema;
/* ------------------------------------------------------------- tenant settings ---- */
/**
 * Tenant scheduling settings (FR-PLAT-06, M4.12).
 *
 * `timeZone` and `statesServed` are columns on `tenants`; the rest live in
 * `tenants.settings` under the `scheduling` key. `SCHEDULING_UI.md` §5.4 asked how the
 * tenant window relates to the per-type window: **the tenant setting is the floor and a
 * type may only narrow it**, which is what `resolveBookingWindow()` implements.
 */
export const SchedulingSettingsSchema = z.object({
    timeZone: TimeZoneSchema,
    statesServed: z.array(StateCodeSchema),
    /** A patient may not book anything sooner than this many days from now. */
    minLeadDays: z.number().int().min(0).max(365),
    /** Nor further out than this many days. */
    maxHorizonDays: z.number().int().min(1).max(365),
    /** Days the clinic offers at all, whatever a provider's own week says. */
    weekdaysOffered: z.array(WeekdaySchema),
    /**
     * EHR statuses that mean "the visit happened" (ADR-010, Q-P13). `sync.appointment_status`
     * raises `appointment.completed` when a mirrored status is one of these.
     */
    ehrCompletionStatuses: z.array(z.string().min(1).max(40)),
    /** EHR statuses that mean the patient did not attend. */
    ehrNoShowStatuses: z.array(z.string().min(1).max(40)),
    /** Minutes a slot is held during checkout (ADR-009: 10 minutes by default). */
    reservationMinutes: z.number().int().min(1).max(60),
    /**
     * How stale synced busy time may be before the booking flow stops promising a
     * confirmed time (INTEGRATIONS.md §1 "Availability while Tebra is down").
     */
    busyBlockStalenessMinutes: z.number().int().min(5).max(1440),
});
export const SCHEDULING_SETTINGS_DEFAULTS = Object.freeze({
    timeZone: 'America/Los_Angeles',
    statesServed: [],
    minLeadDays: 1,
    maxHorizonDays: 60,
    weekdaysOffered: [1, 2, 3, 4, 5],
    ehrCompletionStatuses: ['CheckedOut'],
    ehrNoShowStatuses: ['NoShow', 'No-Show'],
    reservationMinutes: 10,
    busyBlockStalenessMinutes: 120,
});
export const UpdateSchedulingSettingsRequestSchema = SchedulingSettingsSchema.partial().refine((value) => value.minLeadDays === undefined ||
    value.maxHorizonDays === undefined ||
    value.minLeadDays <= value.maxHorizonDays, { message: 'The booking window must end after it begins.', path: ['maxHorizonDays'] });
/* ------------------------------------------------------------------- engine I/O ---- */
/**
 * Time already taken: EHR busy blocks, local appointments and live reservations. The
 * engine treats all three identically (ARCHITECTURE §8.1 step 2).
 */
export const BusyBlockSchema = z.object({
    providerId: z.string(),
    startAt: z.string(),
    endAt: z.string(),
    source: z.enum(['ehr', 'appointment', 'reservation']),
    /** Operational label only. Never a patient name, never a reason for the visit. */
    label: z.string(),
});
/** Why a day is empty. Admin-only wording; a patient is never told. */
export const EmptyDayReasonSchema = z.enum([
    'outside-booking-window',
    'weekday-not-offered',
    'clinic-closed',
    'no-provider-licensed',
    'no-provider-working',
    'fully-booked',
]);
export const AvailabilitySlotSchema = z.object({
    startAt: z.string(),
    endAt: z.string(),
    /** `HH:MM` in the clinic zone - the time a patient sees. */
    localTime: LocalTimeSchema,
    /**
     * Candidate providers, in assignment order. Present on the **admin** preview only;
     * the public surface never carries it (ARCHITECTURE §8.1 step 3).
     */
    providerIds: z.array(z.string()).optional(),
});
export const AvailabilityDaySchema = z.object({
    date: LocalDateSchema,
    weekday: WeekdaySchema,
    slots: z.array(AvailabilitySlotSchema),
    emptyReason: EmptyDayReasonSchema.nullable(),
});
/* ---------------------------------------------------------------- public queries ---- */
/**
 * `GET /public/availability/dates` and `/slots`.
 *
 * `type` accepts either the appointment type's uuid or its `key`; the funnel knows the
 * key from `GET /public/tenant` and should not have to carry a uuid through the URL.
 */
export const PublicAvailabilityDatesQuerySchema = z.object({
    type: z.string().min(1),
    state: StateCodeSchema,
    from: LocalDateSchema.optional(),
    to: LocalDateSchema.optional(),
});
export const PublicAvailabilitySlotsQuerySchema = z.object({
    type: z.string().min(1),
    state: StateCodeSchema,
    date: LocalDateSchema,
});
export const PublicAvailabilityDatesResponseSchema = z.object({
    data: z.array(z.object({ date: LocalDateSchema, slotCount: z.number().int().min(0) })),
    clinicTimeZone: z.string(),
});
export const PublicAvailabilitySlotsResponseSchema = z.object({
    data: z.array(AvailabilitySlotSchema.omit({ providerIds: true })),
    clinicTimeZone: z.string(),
});
/** `GET /admin/availability/preview` - the same engine, with the provider shown. */
export const AdminAvailabilityPreviewQuerySchema = z
    .object({
    from: LocalDateSchema,
    to: LocalDateSchema,
    type: z.string().min(1),
    state: StateCodeSchema,
})
    .refine((value) => value.to >= value.from, {
    message: 'The range must end on or after it begins.',
    path: ['to'],
});
export const AdminAvailabilityPreviewResponseSchema = z.object({
    data: z.array(AvailabilityDaySchema),
    clinicTimeZone: z.string(),
    /** Age of the newest EHR busy-block sync, so staff can see stale data as stale. */
    busyBlocksSyncedAt: z.string().nullable(),
});
/* ----------------------------------------------------------------- reservations ---- */
export const CreateReservationRequestSchema = z.object({
    /** Uuid or `key` of the appointment type, same as the availability query. */
    appointmentTypeId: z.string().min(1),
    startAt: z.iso.datetime({ offset: true }),
    state: StateCodeSchema,
    /** Present once the funnel has a draft (ADR-018); null before M5.2 wires drafts. */
    draftId: z.uuid().nullable().default(null),
});
export const ReservationSchema = z.object({
    id: z.string(),
    appointmentTypeId: z.string(),
    draftId: z.string().nullable(),
    startAt: z.string(),
    endAt: z.string(),
    clinicTimeZone: z.string(),
    expiresAt: z.string(),
    /** False once the single permitted extension has been used (API.md §2.3). */
    extendable: z.boolean(),
});
export const ReservationResponseSchema = z.object({
    data: z.object({ reservation: ReservationSchema }),
});
/** `details` of a `409 SLOT_TAKEN` (API.md §3.3). */
export const SlotTakenDetailsSchema = z.object({
    reservationId: z.string().optional(),
    alternatives: z.array(z.object({ startAt: z.string(), endAt: z.string() })),
    draftPreserved: z.boolean(),
});
/* --------------------------------------------------------------------- bookings ---- */
/**
 * The patient details half of `POST /public/bookings/confirm` (API.md §3.2).
 *
 * Insurance, payment method and intake arrive in M6/M7/M8 and attach to the same
 * appointment afterwards; accepting them now would mean a schema the funnel could fill
 * in with fields nothing reads.
 */
export const BookingPatientSchema = z.object({
    firstName: z.string().min(1).max(80),
    lastName: z.string().min(1).max(80),
    dateOfBirth: LocalDateSchema,
    email: z.email(),
    phone: z
        .string()
        .regex(/^\+[1-9]\d{7,14}$/, 'Enter a phone number in international format.')
        .nullable()
        .default(null),
    sex: z.enum(['female', 'male', 'other', 'unknown']).default('unknown'),
    state: StateCodeSchema,
    timeZone: TimeZoneSchema,
    preferredLanguage: z.string().min(2).max(10).default('en'),
});
export const ConfirmBookingRequestSchema = z.object({
    reservationId: z.uuid(),
    draftId: z.uuid().nullable().default(null),
    patient: BookingPatientSchema,
    /**
     * Consent kinds the patient accepted in the funnel; recorded append-only.
     *
     * `marketing_authorization` joined the list in M9.7 (ADR-028, FR-CRM-03): the funnel
     * spells it `marketing` on the draft and it becomes the HIPAA 45 CFR 164.508
     * authorization here. Before that it was dropped on the way out of the draft, because
     * `ck_consents_kind` had no word for it.
     */
    consents: z.array(ConsentKindSchema).default([]),
});
export const APPOINTMENT_STATUSES = [
    'pending_ehr',
    'confirmed',
    'needs_attention',
    'checked_in',
    'completed',
    'no_show',
    'cancelled',
    'rescheduled',
];
export const AppointmentStatusSchema = z.enum(APPOINTMENT_STATUSES);
export const BookingAppointmentSchema = z.object({
    id: z.string(),
    bookingReference: z.string(),
    status: AppointmentStatusSchema,
    startAt: z.string(),
    endAt: z.string(),
    clinicTimeZone: z.string(),
    patientTimeZone: z.string(),
    appointmentType: z.object({ id: z.string(), key: z.string(), name: z.string() }),
    policy: z.object({
        windowHours: z.number().int(),
        lateCancelFeeCents: z.number().int(),
        noShowFeeCents: z.number().int(),
        currency: z.string(),
        patientText: z.string().nullable(),
    }),
});
export const ConfirmBookingResponseSchema = z.object({
    data: z.object({
        appointment: BookingAppointmentSchema,
        portalLink: z.string(),
    }),
});
/* -------------------------------------------------------- admin appointment ops ---- */
/** Who did a thing, on a timeline row or on an appointment. */
export const ACTOR_KINDS = ['patient', 'staff', 'system'];
export const ActorKindSchema = z.enum(ACTOR_KINDS);
/**
 * The patient, as an **appointment** surface is allowed to show them (M4.13).
 *
 * A name and two masked contact points, and nothing else. The list renders forty rows at
 * a time on a screen in a clinic reception; a full address book of email addresses and
 * phone numbers on it is data at rest on a monitor, and the record screen - which a
 * staff member has to choose to open, and which is behind `patients.read` - is where the
 * unmasked values belong (rules §25.1).
 */
export const AdminAppointmentPatientSchema = z
    .object({
    id: z.string(),
    /** `firstName lastName`, rendered by the server so every surface agrees. */
    name: z.string(),
    /** `j•••@example.com`. Enough to recognise, not enough to copy. */
    emailMasked: z.string(),
    /** `•••••1234`, or null when the patient gave no number. */
    phoneMasked: z.string().nullable(),
})
    .strict();
/**
 * The visit's money, reduced to what a list cell and a detail header show.
 *
 * Deliberately not `ChargeIntent`: that shape carries the override reason and the person
 * who typed it, which belong on the payment panel behind `payments.read`. This is the
 * status, the number and the currency, which is what a scheduling screen is asking.
 */
export const AppointmentChargeSummarySchema = z
    .object({
    id: z.string(),
    kind: z.enum(['visit', 'late_cancel', 'no_show']),
    status: z.string(),
    amountCents: z.int().min(0),
    currency: z.string().length(3),
    /** A class, never the processor's sentence. */
    lastErrorClass: z.string().nullable(),
})
    .strict();
/**
 * Where this visit stands with the clinic's EHR.
 *
 * Derived from `appointments.ehr_*` rather than stored, so it cannot disagree with them:
 * `disabled` when the tenant has no EHR install at all, which is the case a panel
 * reading `ehrExternalId === null` alone gets wrong (it reads as "failed").
 */
export const EHR_SYNC_STATES = ['synced', 'pending', 'failed', 'disabled'];
export const EhrSyncStateSchema = z
    .object({
    /** False for a tenant with no EHR integration; the panel then explains itself. */
    enabled: z.boolean(),
    status: z.enum(EHR_SYNC_STATES),
    externalId: z.string().nullable(),
    lastSyncAt: z.iso.datetime().nullable(),
    /** `appointments.ehr_status_raw`, e.g. Tebra's `CheckedOut`. */
    rawStatus: z.string().nullable(),
    /** The failure class from the last attempt. Never the vendor's sentence. */
    message: z.string().nullable(),
})
    .strict();
/** The next message queued for this visit, so a list can show "reminder in 2 days". */
export const NextNotificationSchema = z
    .object({
    id: z.string(),
    kind: z.string(),
    channel: z.enum(['email', 'sms']),
    status: z.string(),
    scheduledFor: z.iso.datetime().nullable(),
})
    .strict();
export const AdminRescheduleRequestSchema = z.object({
    startAt: z.iso.datetime({ offset: true }),
    providerId: z.uuid().nullable().default(null),
    notifyPatient: z.boolean().default(true),
    reason: z.string().max(200).default(''),
});
export const AdminCancelRequestSchema = z.object({
    reason: z.string().min(1).max(200),
    /** Records the late-cancel fee decision; the charge intent itself is M7. */
    applyFee: z.boolean().default(false),
    notifyPatient: z.boolean().default(true),
});
export const AdminCompleteRequestSchema = z.object({
    completedAt: z.iso.datetime({ offset: true }).nullable().default(null),
});
export const AdminNoShowRequestSchema = z.object({
    /** Whether the tenant's no-show fee applies; stored for M7 to act on. */
    applyFee: z.boolean().default(false),
});
export const ReassignProviderRequestSchema = z.object({
    providerId: z.uuid(),
    reason: z.string().max(200).default(''),
});
export const AdminAppointmentSchema = BookingAppointmentSchema.extend({
    /**
     * The pre-visit readiness indicator (M8.9, FR-INTAKE-07).
     *
     * Optional and additive: it is computed by `ReadinessService` on the appointment read
     * path and is absent from the surfaces that have not been taught to ask for it yet.
     * A client that does not know about it ignores it (`API.md` §1.10).
     */
    readiness: ReadinessSchema.optional(),
    providerId: z.string(),
    providerName: z.string(),
    patientId: z.string(),
    patientState: z.string(),
    ehrExternalId: z.string().nullable(),
    ehrStatusRaw: z.string().nullable(),
    ehrSyncedAt: z.string().nullable(),
    source: z.string(),
    cancelledAt: z.string().nullable(),
    cancelReason: z.string().nullable(),
    completedAt: z.string().nullable(),
    /* ---------------------------------------------------- M4.13 / M11.2 additions ---- */
    /**
     * Everything below is **optional and additive** (API.md §1.10).
     *
     * `GET /admin/appointments/:id` fills them in; the five action routes return the
     * appointment through the same schema and do not, because a reschedule answers "it
     * moved" and re-reading four other tables to say so would make every action pay for
     * the detail screen. A client that does not know about them ignores them, and a client
     * that does asks the detail route.
     */
    /** When the visit was booked. The detail header's "booked on" line. */
    createdAt: z.iso.datetime().optional(),
    /** The room for a telehealth visit. Null when the provider has not been given one. */
    telehealthUrl: z.string().nullable().optional(),
    /** The appointment this one replaced, for the "rescheduled from" line. */
    rescheduledFromId: z.string().nullable().optional(),
    cancelledByKind: ActorKindSchema.nullable().optional(),
    patient: AdminAppointmentPatientSchema.optional(),
    /** The visit's money, at a glance. The whole history is `GET …/payments` (M7.5). */
    chargeIntent: AppointmentChargeSummarySchema.nullable().optional(),
    ehr: EhrSyncStateSchema.optional(),
});
/* --------------------------------------------------------------------- envelopes ---- */
export const ProviderListResponseSchema = z.object({ data: z.array(ProviderSummarySchema) });
export const ProviderResponseSchema = z.object({ data: ProviderSummarySchema });
export const ProviderDetailResponseSchema = z.object({ data: ProviderDetailSchema });
export const AppointmentTypeListResponseSchema = z.object({
    data: z.array(AppointmentTypeSchema),
});
export const AppointmentTypeResponseSchema = z.object({ data: AppointmentTypeSchema });
export const ScheduleExceptionListResponseSchema = z.object({
    data: z.array(ScheduleExceptionSchema),
});
export const ScheduleExceptionResponseSchema = z.object({ data: ScheduleExceptionSchema });
export const SchedulingSettingsResponseSchema = z.object({ data: SchedulingSettingsSchema });
export const AdminAppointmentResponseSchema = z.object({ data: AdminAppointmentSchema });
/* --------------------------------------------------------------------- helpers ---- */
/**
 * The parity `provider_schedules.week_parity` would carry for an anchor date, kept so a
 * reader of the legacy column is not misled. **Derived, never authoritative** (Q-T22).
 */
export function weekParityOf(startDateLocal) {
    return isoWeekNumber(startDateLocal) % 2 === 0 ? 'even' : 'odd';
}
const DAY_MS = 86_400_000;
function isoWeekNumber(dateLocal) {
    const date = new Date(`${dateLocal}T12:00:00Z`);
    const day = (date.getUTCDay() + 6) % 7;
    date.setUTCDate(date.getUTCDate() - day + 3);
    const firstThursday = Date.UTC(date.getUTCFullYear(), 0, 4);
    const firstDay = (new Date(firstThursday).getUTCDay() + 6) % 7;
    const weekOne = firstThursday - firstDay * DAY_MS + 3 * DAY_MS;
    return 1 + Math.round((date.getTime() - weekOne) / (7 * DAY_MS));
}
/**
 * The booking window a (tenant, type) pair actually uses.
 *
 * `SCHEDULING_UI.md` §5.4: the tenant setting is the floor and a type may only narrow
 * it. So the lead is the **later** of the two and the horizon the **earlier**; a type
 * cannot open bookings sooner or further out than the clinic allows.
 */
export function resolveBookingWindow(settings, type) {
    return {
        minLeadMinutes: Math.max(settings.minLeadDays * 24 * 60, type.minLeadHours * 60),
        maxHorizonDays: Math.min(settings.maxHorizonDays, type.maxHorizonDays),
    };
}
/* ------------------------------------------------- M4.13 admin appointment list ---- */
/**
 * A row of `GET /admin/appointments` (API.md §2.5, FR-ADMIN-03).
 *
 * Wider than `BookingAppointment` and narrower than `AdminAppointment`: a list cell
 * needs the patient, the clinician, the traffic light and the three "is anything wrong"
 * signals (money, EHR, the next message), and nothing else. Each of the four composed
 * fields is one batched query for the whole page, never one per row - twenty-five rows
 * times four subsystems is a hundred round trips for a screen with a one-second budget.
 */
export const AdminAppointmentRowSchema = z
    .object({
    id: z.string(),
    bookingReference: z.string(),
    startAt: z.iso.datetime(),
    endAt: z.iso.datetime(),
    clinicTimeZone: z.string(),
    patientTimeZone: z.string(),
    status: AppointmentStatusSchema,
    source: z.string(),
    createdAt: z.iso.datetime(),
    patient: AdminAppointmentPatientSchema,
    appointmentType: z.object({ id: z.string(), key: z.string(), name: z.string() }),
    /** Never null in practice - `provider_id` is NOT NULL - but named for the UI's shape. */
    provider: z.object({ id: z.string(), name: z.string() }).nullable(),
    readiness: ReadinessSchema,
    chargeIntent: AppointmentChargeSummarySchema.nullable(),
    ehr: EhrSyncStateSchema,
    nextNotification: NextNotificationSchema.nullable(),
})
    .strict();
/** Allowed sorts. Anything else is `400 VALIDATION_FAILED` (API.md §1.7). */
export const ADMIN_APPOINTMENT_SORTS = [
    'startAt',
    '-startAt',
    'createdAt',
    '-createdAt',
    'status',
];
export const AdminAppointmentSortSchema = z.enum(ADMIN_APPOINTMENT_SORTS);
/**
 * The readiness filter (ADMIN_OPERATIONS_UI.md §6 item 7).
 *
 * `ready` is all three legs green; `not_ready` is anything else. It is a filter over a
 * value computed from four other tables rather than a column, so the service pages the
 * appointments and drops the rows that do not match - the cursor advances over every row
 * *examined*, which is what keeps paging correct while the filter throws rows away.
 */
export const READINESS_FILTERS = ['any', 'ready', 'not_ready'];
export const ReadinessFilterSchema = z.enum(READINESS_FILTERS);
/**
 * `GET /admin/appointments` (API.md §1.7, §2.5).
 *
 * `status` is repeated on the wire (`?status=confirmed&status=completed`); Fastify hands
 * a single value as a string and several as an array, so the schema accepts both.
 *
 * `q` searches the booking reference and `patients.search_key`, both trigram-indexed.
 * **It is never logged**: a search box over a patient list is a stream of names, and a
 * log line is the one place PHI is hardest to delete afterwards (SECURITY.md §9).
 */
export const AdminAppointmentListQuerySchema = z
    .object({
    status: z
        .union([AppointmentStatusSchema, z.array(AppointmentStatusSchema)])
        .transform((value) => (Array.isArray(value) ? value : [value]))
        .optional(),
    providerId: z.uuid().optional(),
    appointmentTypeId: z.uuid().optional(),
    /** Inclusive. An instant, so a range means the same thing in every zone. */
    from: z.iso.datetime({ offset: true }).optional(),
    /** Exclusive. */
    to: z.iso.datetime({ offset: true }).optional(),
    q: z.string().min(1).max(120).optional(),
    readiness: ReadinessFilterSchema.default('any'),
    sort: AdminAppointmentSortSchema.default('startAt'),
    limit: z.coerce.number().int().min(1).max(PAGE_LIMIT_MAX).default(PAGE_LIMIT_DEFAULT),
    cursor: z.string().min(1).optional(),
})
    .strict();
export const AdminAppointmentListResponseSchema = z
    .object({ data: z.array(AdminAppointmentRowSchema), page: PageInfoSchema })
    .strict();
/* ------------------------------------------------------------ M11.2 the timeline ---- */
/**
 * `appointment_events.kind`, plus the kinds this milestone merges in from the other
 * subsystems' own tables.
 *
 * The timeline is **one ordered list**, not six panels: staff read it to answer "what
 * happened to this visit, in order", and an eligibility check that only appears on the
 * insurance tab is invisible at exactly the moment somebody is reconstructing a mistake.
 * Six of the kinds below are therefore synthesised from `eligibility_checks`,
 * `charge_intents`, `charges`, `refunds`, `intake_submissions` and `notifications`
 * rather than read from `appointment_events`.
 *
 * Consumers must tolerate values they do not know (API.md §1.10).
 */
export const APPOINTMENT_EVENT_KINDS = [
    'created',
    'ehr_written',
    'ehr_failed',
    'ehr_retry_queued',
    'status_changed',
    'rescheduled',
    'cancelled',
    'reminder_scheduled',
    'notification_sent',
    'notification_delivered',
    'notification_failed',
    'eligibility_checked',
    'quote_issued',
    'charge_scheduled',
    'charged',
    'charge_failed',
    'refunded',
    'intake_submitted',
    'intake_reviewed',
    'note',
    'waitlist_offered',
    /**
     * M9.15. What the EHR did, read back by the poll (`_research/tebra-communications.md`
     * §2.2). Tebra has no webhooks and no message log we can read, so these three are the
     * whole of what its side of the conversation looks like from here: the patient
     * confirmed from a Tebra reminder (`ConfirmationStatus = Confirmed`), Tebra recorded
     * that it sent one (`AppointmentStatus = ReminderSent`), or the appointment was
     * cancelled in Tebra (`ConfirmationStatus = Cancelled`). They arrive at poll latency,
     * carry no channel and no delivery state, and are the reason a clinic on
     * `reminderOwner = ehr` still has *something* on the timeline.
     */
    'ehr_confirmed',
    'ehr_reminder_sent',
    'ehr_cancelled',
];
export const AppointmentEventKindSchema = z.enum(APPOINTMENT_EVENT_KINDS);
/**
 * One row of the merged timeline.
 *
 * `summary` is rendered by the server, from ids, statuses and amounts. It never carries
 * clinical detail, and `note` - the one free-text field in the whole subsystem - is
 * decrypted here and nowhere else (`appointment_events.note_enc`, API.md §2.5).
 */
export const AppointmentTimelineEventSchema = z
    .object({
    id: z.string(),
    kind: AppointmentEventKindSchema,
    /** Named `occurredAt` to match `appointment_events.occurred_at` and the admin seam. */
    occurredAt: z.iso.datetime(),
    actorKind: ActorKindSchema,
    actorId: z.string().nullable(),
    /** The actor's name, resolved by the server. "System" for an unattended change. */
    actorLabel: z.string(),
    /** One short sentence. No clinical detail. */
    summary: z.string(),
    /** Present only on a `note`, and only for an actor who may decrypt it. */
    note: z.string().optional(),
    correlationId: z.string().nullable(),
})
    .strict();
export const AppointmentTimelineResponseSchema = z
    .object({ data: z.array(AppointmentTimelineEventSchema), page: PageInfoSchema })
    .strict();
/**
 * `POST /admin/appointments/:id/notes` (`appointments.update`).
 *
 * The body is encrypted into `appointment_events.note_enc` and the row's `data` column
 * carries only its length, because `data` is the half the audit trigger keeps in the
 * clear. A minimum of one character and a ceiling of 2000: a note is a sentence about a
 * visit, not a chart.
 */
export const AddAppointmentNoteRequestSchema = z
    .object({ body: z.string().trim().min(1).max(2000) })
    .strict();
export const AppointmentTimelineEventResponseSchema = z
    .object({ data: AppointmentTimelineEventSchema })
    .strict();
/* ------------------------------------------------------- M4.3 reschedule slots ---- */
/**
 * One bookable slot for the staff reschedule dialog
 * (ADMIN_OPERATIONS_UI.md §6 item 1).
 *
 * The provider is **named**, which is the whole reason this route exists beside
 * `GET /public/availability/slots`: a patient picking a time does not choose a
 * clinician (ADR-009, the engine assigns), and a staff member moving a visit does.
 */
export const RescheduleSlotSchema = z
    .object({
    startAt: z.iso.datetime(),
    endAt: z.iso.datetime(),
    localTime: LocalTimeSchema,
    providerId: z.string(),
    providerName: z.string(),
    /** True when this is the clinician the visit is already with. */
    isCurrentProvider: z.boolean(),
})
    .strict();
export const RescheduleSlotsQuerySchema = z.object({ date: LocalDateSchema }).strict();
export const RescheduleSlotsResponseSchema = z
    .object({
    data: z.array(RescheduleSlotSchema),
    /** Why the day is empty, when it is. Admin wording; a patient never sees it. */
    emptyReason: EmptyDayReasonSchema.nullable(),
})
    .strict();
/* ------------------------------------------------------------------ EHR directory ---- */
/**
 * `GET /admin/ehr/directory` - the connected record system's providers and visit
 * reasons, read live, so staff can link a provider (`Provider.ehrExternalId`) and a visit
 * type (`AppointmentType.ehrReasonId`) by picking from a list instead of typing an id.
 *
 * `connected: false` is an ordinary answer (no EHR install, or not enabled), not an
 * error. `reasonsSupported` is false for an EHR adapter with no reason list.
 */
export const EhrDirectoryProviderSchema = z
    .object({
    externalId: z.string(),
    displayName: z.string(),
    active: z.boolean(),
})
    .strict();
export const EhrDirectoryReasonSchema = z
    .object({
    externalId: z.string(),
    name: z.string(),
    durationMinutes: z.number().int().nullable(),
})
    .strict();
export const EhrDirectorySchema = z
    .object({
    connected: z.boolean(),
    /** The EHR's display name, e.g. `Tebra`, for labels. Null when not connected. */
    vendorName: z.string().nullable(),
    providers: z.array(EhrDirectoryProviderSchema),
    reasons: z.array(EhrDirectoryReasonSchema),
    reasonsSupported: z.boolean(),
})
    .strict();
export const EhrDirectoryResponseSchema = z.object({ data: EhrDirectorySchema }).strict();
//# sourceMappingURL=scheduling.js.map